import {ReactNode, createContext, useContext, useMemo} from 'react';

import {UserContext} from '../UserContext/UserContext';

import {Survey, Surveys} from '../../features/company/culture/surveys/types/SurveyTypes';
import {useApiRead} from '../../hooks/useApiRead';
import {GetRoutes} from '../../lib/api/GET/GetRoutes';
import {CompanyAdminRoleData} from '../../types/User';
import {calculateDaysLeft} from '../../utils/dateUtils/calculateDaysUntilDate';

export interface SurveyContextState {
  cultureSurveys: Survey[];

  // NOTE: - Happiness part left out for the first version
  // happinessSurveys: Survey[];

  daysLeft: number | null;
  findCurrentSurvey: (surveys: Survey[]) => Survey | undefined;
  getPreviousSurvey: (surveys: Survey[]) => Survey | undefined;
  getUpcomingSurvey: (surveys: Survey[]) => Survey | undefined;
  fetchSurveys: () => void;
  loading: boolean;
  error: string | null;
}

const defaultContext: SurveyContextState = {
  cultureSurveys: [],

  // NOTE: - Happiness part left out for the first version
  // happinessSurveys: [],

  daysLeft: null,
  findCurrentSurvey: () => undefined,
  getPreviousSurvey: () => undefined,
  getUpcomingSurvey: () => undefined,
  fetchSurveys: () => {
    return;
  },
  loading: false,
  error: null,
};

interface Props {
  children: ReactNode;
}

const SurveyContext = createContext<SurveyContextState>(defaultContext);

export const useSurvey = () => useContext(SurveyContext);

export const SurveyProvider = ({children}: Props) => {
  // NOTE: - Happiness part left out for the first version
  // const [happinessSurveys, setHappinessSurveys] = useState<Survey[]>([]);

  const {currentUser} = useContext(UserContext);
  const companyId = useMemo(() => {
    return (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  }, [currentUser]);

  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useApiRead<Surveys>({path: GetRoutes.getCompanySurveys(companyId)});

  const orderByDate = (surveys: Survey[]) => {
    return surveys.sort((a, b) => {
      return new Date(a.startsAt ? a.startsAt : 0).getTime() - new Date(b.startsAt ? b.startsAt : 0).getTime();
    });
  };

  const getUpcomingSurvey = (surveys: Survey[]) => {
    const now = new Date(); // Current date in the user's local time zone

    const upcomingSurvey = surveys?.find((survey) => {
      if (survey.startsAt) {
        const startsAtDate = new Date(survey.startsAt);

        // Ensure that both dates are in UTC for accurate comparison
        const nowUTC = new Date(now.toISOString());
        const startsAtUTC = new Date(startsAtDate.toISOString());

        // Remove hours and minutes from the dates
        startsAtUTC.setUTCHours(0, 0, 0, 0);
        nowUTC.setUTCHours(0, 0, 0, 0);

        return startsAtUTC > nowUTC;
      }
      return false;
    });

    return upcomingSurvey;
  };

  const daysLeft = getUpcomingSurvey(data?.cultureSurveys || [])
    ? calculateDaysLeft(getUpcomingSurvey(data?.cultureSurveys || []))
    : null;

  const findCurrentSurvey = (surveys: Survey[]) => {
    return surveys.find((survey: Survey) => {
      if (survey.startedAt) {
        const startedAtDate = new Date(survey.startedAt);

        // Set hours, minutes, seconds, and milliseconds to zero
        startedAtDate.setHours(0, 0, 0, 0);

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        return startedAtDate <= currentDate && !survey.endedAt;
      }

      return false;
    });
  };

  const getPreviousSurvey = (surveys: Survey[]) => {
    const previousSurvey = surveys
      .sort((a, b) => {
        const endedAtA = a.endedAt ? new Date(a.endedAt) : new Date(0);
        const endedAtB = b.endedAt ? new Date(b.endedAt) : new Date(0);

        // Set hours, minutes, seconds, and milliseconds to zero
        endedAtA.setHours(0, 0, 0, 0);
        endedAtB.setHours(0, 0, 0, 0);

        return endedAtA.getTime() - endedAtB.getTime();
      })
      .filter((survey) => {
        if (survey.endedAt) {
          const endedAtDate = new Date(survey.endedAt);

          // Set hours, minutes, seconds, and milliseconds to zero
          endedAtDate.setHours(0, 0, 0, 0);

          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);

          return endedAtDate <= currentDate;
        }

        return false;
      });

    return previousSurvey?.[previousSurvey.length - 1];
  };

  const value: SurveyContextState = {
    cultureSurveys: orderByDate(data?.cultureSurveys || []),

    // NOTE: - Happiness part left out for the first version
    // happinessSurveys,

    daysLeft,
    findCurrentSurvey,
    getPreviousSurvey,
    getUpcomingSurvey,
    fetchSurveys: refetch,
    loading,
    error: error?.message ?? null,
  };

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
};
